import React from 'react';
import styled from 'styled-components';
import { ProductMedia } from 'data/Product';
import documentIcon from 'images/document-icon.svg';
import { Color, FontSize, FontWeight, Percent } from 'lib/styles';
import { Main } from 'styles/themeColors';

const Name = styled.h1`
  font-size: 28px;
  font-weight: ${FontWeight.bold};
  margin: 0 0 10px;
  line-height: 34px;
`;

const Price = styled.div`
  font-size: ${FontSize.large};
  margin-bottom: 25px;
`;

const LogoWrapper = styled.div`
  padding: 30px 0;
  border-top: 1px solid ${Color.veryLightGray};
`;

const Logo = styled.img`
  width: ${Percent.full};
  max-width: 210px;
`;

const Description = styled.div`
  padding: 30px 0;
  border-top: 1px solid ${Color.veryLightGray};

  ul {
    padding-left: 15px;
    list-style-type: disc;
    margin-left: 20px;
  }

  ol {
    padding-left: 15px;
    list-style-type: decimal;
    margin-left: 20px;
  }
`;

const ProductDetails = styled.div`
  padding: 30px 0;
  border-bottom: 1px solid ${Color.veryLightGray};
`;

const DetailKey = styled.span`
  display: inline-block;
  font-weight: ${FontWeight.bold};
  margin-right: 1em;
`;

const Documents = styled.ul`
  list-style: none;
  padding: 0;
`;

const Document = styled.li``;

const DocumentLink = styled.a`
  display: block;
  color: ${Main.accent};
  padding: 5px 0 8px;

  &::before {
    content: '';
    display: inline-block;
    width: 20px;
    height: 20px;
    background-image: url(${documentIcon});
    background-repeat: no-repeat;
    vertical-align: middle;
    margin-right: 15px;
    margin-bottom: 3px;
  }
`;

interface Props {
  documents: ProductMedia[];
  logo?: string;
  description: string;
  name: string;
  price: string;
  productDetails: Record<string, any>;
};

// Fix Quill editor bullet lists that are stored as <ol> with data-list="bullet"
function fixQuillBulletLists(html: string): string {
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all <ol> elements
  const orderedLists = tempDiv.querySelectorAll('ol');

  orderedLists.forEach((ol) => {
    // Check if this <ol> contains both bullet and ordered items
    const bulletItems = ol.querySelectorAll('li[data-list="bullet"]');
    const orderedItems = ol.querySelectorAll('li[data-list="ordered"]');
    const hasBulletItems = bulletItems.length > 0;
    const hasOrderedItems = orderedItems.length > 0;

    // If the list has both bullet and ordered items, split it
    if (hasBulletItems && hasOrderedItems) {
      // Create new lists
      const ul = document.createElement('ul');
      const newOl = document.createElement('ol');

      // Process each child in order
      Array.from(ol.children).forEach(child => {
        if (child.tagName === 'LI') {
          const dataList = child.getAttribute('data-list');
          const isBullet = dataList === 'bullet';

          // Clone the child
          const clonedChild = child.cloneNode(true) as HTMLElement;

          // Remove data-list attribute
          clonedChild.removeAttribute('data-list');

          // Remove ql-ui spans
          const qlUiSpans = clonedChild.querySelectorAll('span.ql-ui');
          qlUiSpans.forEach(span => span.remove());

          // Add to appropriate list
          if (isBullet) {
            ul.appendChild(clonedChild);
          } else {
            newOl.appendChild(clonedChild);
          }
        }
      });

      // Create a wrapper to hold both lists
      const wrapper = document.createDocumentFragment();

      // Add lists in the order they first appeared in the original
      const firstIsBullet = ol.children[0]?.getAttribute('data-list') === 'bullet';

      if (firstIsBullet) {
        wrapper.appendChild(ul);
        if (newOl.children.length > 0) wrapper.appendChild(newOl);
      } else {
        if (newOl.children.length > 0) wrapper.appendChild(newOl);
        wrapper.appendChild(ul);
      }

      // Replace the original <ol> with the split lists
      ol.replaceWith(wrapper);
    } else if (hasBulletItems) {
      // Only bullet items - convert entire <ol> to <ul>
      const ul = document.createElement('ul');

      Array.from(ol.attributes).forEach(attr => {
        if (attr.name !== 'data-list') {
          ul.setAttribute(attr.name, attr.value);
        }
      });

      while (ol.firstChild) {
        const li = ol.firstChild as HTMLElement;
        if (li.nodeType === Node.ELEMENT_NODE && li.tagName === 'LI') {
          li.removeAttribute('data-list');
          const qlUiSpans = li.querySelectorAll('span.ql-ui');
          qlUiSpans.forEach(span => span.remove());
        }
        ul.appendChild(li);
      }

      ol.replaceWith(ul);
    }
    // If only ordered items, leave as <ol> (no changes needed)
  });

  return tempDiv.innerHTML;
}

export default function PDPPreviewDetails({
  documents,
  logo,
  description,
  name,
  price,
  productDetails
}: Props) {
  let documentItems = null;
  if (documents) {
    documentItems = documents.map(document => (
      <Document key={document.displayName}>
        <DocumentLink href={process.env.REACT_APP_MEDIA_URL + document.path} target="_blank">
          View {document.displayName}
        </DocumentLink>
      </Document>
    ));
  }

  const details = Object.keys(productDetails).map(key => {
    const value = productDetails[key];
    if (value !== null && value !== undefined && value.length > 0) {
      return (
        <div key={key}>
          <DetailKey>{key}:</DetailKey>
          <span>{productDetails[key]}</span>
        </div>
      );
    }
    return null;
  });

  // Fix Quill editor bullet lists that are incorrectly stored as <ol>
  const fixedDescription = fixQuillBulletLists(description);

  return (
    <>
      <Name>{name}</Name>
      <Price>{price}</Price>
      <LogoWrapper>
        <Logo src={logo ? logo : ''} />
      </LogoWrapper>
      <Description dangerouslySetInnerHTML={{ __html: fixedDescription }} />
      <ProductDetails>{details}</ProductDetails>
      <Documents>{documentItems}</Documents>
    </>
  );
};
